import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useStudentPortalContext } from "@/contexts/StudentPortalContext";
import { api } from "@/lib/api";
import { Bell, ChevronDown, Pin } from "lucide-react";
import { CATEGORY_BADGE, CATEGORY_LABEL, tr } from "@/lib/labels";
import { PortalEmpty, PortalLoading, PortalPageHeader } from "./StudentPortalParts";

export default function StudentNotices() {
  const { loading: contextLoading, selectedClassId } = useStudentPortalContext();
  const outlet = useOutletContext();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const highlightId = searchParams.get("aviso");

  const load = async () => {
    if (!selectedClassId) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const { data: next } = await api.get("/student/portal/notices", { params: { class_group_id: selectedClassId } });
      setData(next);
      outlet?.setUnread?.(next.unread_count || 0);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [selectedClassId]);

  const markRead = async (notice) => {
    if (notice.is_read) return;
    try {
      await api.post(`/student/portal/notices/${notice.id}/read`);
      setData((current) => current ? {
        ...current,
        unread_count: Math.max((current.unread_count || 0) - 1, 0),
        items: (current.items || []).map((row) => row.id === notice.id ? { ...row, is_read: true, read_at: new Date().toISOString() } : row),
      } : current);
      outlet?.setUnread?.((value) => Math.max((typeof value === "number" ? value : data?.unread_count || 0) - 1, 0));
    } catch (_) {}
  };

  const openNotice = (notice) => {
    setOpenId((current) => current === notice.id ? null : notice.id);
    markRead(notice);
  };

  useEffect(() => {
    if (!highlightId || !data?.items?.length) return;
    const notice = data.items.find((row) => row.id === highlightId);
    if (!notice) return;
    setOpenId(notice.id);
    markRead(notice);
    setTimeout(() => document.getElementById(`aviso-${notice.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId, data?.items?.length]);

  const items = useMemo(() => data?.items || [], [data]);
  if (contextLoading || loading) return <PortalLoading />;

  return <div data-testid="student-notices-v2">
    <PortalPageHeader eyebrow="Comunicação" title="Avisos" description="O ponto laranja mostra o que você ainda não abriu. Um aviso só é marcado como lido quando você o abre." action={data?.unread_count > 0 ? <span className="doxa-badge doxa-badge-orange shrink-0">{data.unread_count} não lido{data.unread_count === 1 ? "" : "s"}</span> : null} />
    {items.length ? <div className="space-y-3">{items.map((notice) => {
      const open = openId === notice.id;
      return <article key={notice.id} id={`aviso-${notice.id}`} className={`doxa-card overflow-hidden transition-shadow ${!notice.is_read ? "ring-1 ring-[#F3C7B5]" : ""}`}>
        <button onClick={() => openNotice(notice)} className="w-full p-4 sm:p-5 text-left flex items-start gap-3" aria-expanded={open}>
          <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${notice.is_read ? "bg-[#D8D0C6]" : "bg-[#F75A1B]"}`} aria-label={notice.is_read ? "Lido" : "Não lido"} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">{notice.is_pinned && <Pin className="w-3.5 h-3.5 text-[#F75A1B]" />}<span className={`doxa-badge ${CATEGORY_BADGE[notice.category || "INFO"]}`}>{tr(CATEGORY_LABEL, notice.category || "INFO")}</span>{!notice.is_read && <span className="text-[11px] font-semibold text-[#F75A1B]">Não lido</span>}</div>
            <h2 className="font-display font-bold mt-2">{notice.title}</h2>
            {!open && <p className="text-sm text-[#6B655E] mt-1 truncate">{notice.content}</p>}
          </div>
          <ChevronDown className={`w-5 h-5 text-[#8A837B] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && <div className="px-4 sm:px-5 pb-5 pl-9 sm:pl-10"><div className="border-t border-[#EEE7DD] pt-4"><p className="text-sm leading-relaxed whitespace-pre-wrap">{notice.content}</p></div></div>}
      </article>;
    })}</div> : <PortalEmpty icon={Bell} title="Sem avisos no momento" description="Quando a equipe publicar um novo aviso para você, ele aparecerá aqui." />}
  </div>;
}
